import startcase from 'lodash.startcase';

/**
 * Filters undefined, null or empty strings as well as boolean false from a list of comma separated items, joins them and returns a string
 * Returns null if 'classes' is empty
 * @param {object} classes - An arguments object
 */
export const setClassNames = (...classes) => {
	return [...classes].filter(className => className).join(' ') || null;
};

/**
 * Copy postfixed preference values to their un-postfixed keys in a preference object.
 * E.g. copy the value of 'titleHeadingColor-sidebar' to 'titleHeadingColor' and remove
 * 	'titleHeadingColor-sidebar' from the object.
 * @param {object} prefsObj - An object of { preference: value, ... }
 * @param {string} modifier - An identifier postfixed to differentiate preferences (e.g. '-sidebar')
 */
export const remapPrefs = (prefsObj, modifier) => {
	const newPrefs = { ...prefsObj };

	// Extract preferences postfixed with modifier
	Object.keys(newPrefs)
		.filter(pref => pref.includes(modifier) && newPrefs[pref] !== '')
		.forEach(pref => {
			newPrefs[pref.replace(modifier, '')] = prefsObj[pref];
		});

	// Remove postfixed preferences and copy new pref values over the original ones
	const filteredPrefs = Object.keys(newPrefs)
		.filter(key => !key.includes(modifier))
		.reduce(
			(obj, key) => ({
				...obj,
				[key]: newPrefs[key]
			}),
			{}
		);

	return filteredPrefs;
};

/**
 * Create formatted search request object
 * @param {string} vehicleId
 * @param {string} accountId
 * @returns {object} - Formatted search request object
 */
export const getSearchRO = (vehicleId, accountId) => {
	return {
		vehicle: {
			rql: `&select(type,make,year,model,trim,bodyStyle,stockNumber,certified,normalFuelType)&eq(uuid,${vehicleId})`
		},
		accounts: {
			rql: `&select(address)&eq(accountId,${accountId})`
		}
	};
};

/**
 * replaces custom tags [city] and [state] with values from account address
 * @param {string} customTitleTemplate - preference value
 * @param {object} accountAddress - from vcda query
 * @returns string
 */
export const getCustomTitle = (customTitleTemplate, accountAddress) => {
	const { city, state } = accountAddress;

	let customTitle = customTitleTemplate.replace(/\[city\]/gi, city); // Replace city
	customTitle = customTitle.replace(/\[state\]/gi, state); // Replace the state

	return customTitle;
};

export const getAccountId = rData => {
	return (
		(rData.parameterMap && rData.parameterMap.accountId) || rData.accountId
	);
};

const vehicleTypeLabelMap = new Map([
	['NEW', 'NEW'],
	['Used', 'WS_USED'], // 'USED' in label services translates to Pre-Owned in english, Hence we use this
	['Pre-Owned', 'PRE_OWNED'],
	['CERTIFIED', 'WS_CERTIFIED_PRE_OWNED'] // The french translation was different for existing labels
]);

export const getVehicleTypeLabel = (label, type, isCertified, usedLabelOpt) => {
	const vehicleType = isCertified ? 'CERTIFIED' : type;

	switch (vehicleType) {
		case 'NEW':
		case 'CERTIFIED':
			return label.get(vehicleTypeLabelMap.get(vehicleType));

		case 'USED':
			return label.get(vehicleTypeLabelMap.get(usedLabelOpt));

		default:
			return startcase(type);
	}
};

/**
 * take array of title attributes from Item Display Config, and returns array of strings split
 * on lineBreak virtual attribute to handle title and subtitle display.
 * @param {object} attrs - array of attribute objects returned from item display config service
 * @returns array of strings
 */
export const getVehicleTitle = attrs => {
	const NEXT_LINE = '\u0085';

	const vehicleTitleAttrs = attrs.reduce((result, { name, itemValue }) => {
		let attr = itemValue;
		if (name === 'lineBreak') {
			attr = NEXT_LINE;
		}
		result.push(attr);
		return result;
	}, []);

	return vehicleTitleAttrs
		.join(' ')
		.split(NEXT_LINE)
		.map(s => s.trim());
};
